import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, ExternalLink, LogOut, ShieldCheck, TriangleAlert } from 'lucide-react'
import HomeShell from './HomeShell'
import DiscordIcon from '@/components/site/DiscordIcon'
import SteamIcon from '@/components/site/SteamIcon'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'
import { HOME_LOGIN_URL, useHomeAuth } from '@/contexts/HomeAuthContext'
import { SUPPORTER_ENABLED } from '@/lib/features'

/**
 * turksletloose.com/profile — where a visitor links Steam and sees their
 * membership.
 *
 * This page exists because the supporter perk is granted on a SteamID64 the
 * game server trusts. Collecting that id at Whop's checkout meant free text
 * nobody verified and the buyer could not correct afterwards; linking it here,
 * through Steam's own sign-in, proves ownership and stays editable.
 *
 * The supporter card follows `SUPPORTER_ENABLED` asymmetrically: while the
 * membership is switched off nobody is offered one here, but anyone who
 * already has a row keeps the card — that card is how they cancel, and
 * hiding it would leave a paying visitor with nothing but Whop's own panel.
 */

/** Whop reports the period end as epoch seconds or an ISO string; accept both. */
function formatDate(value: number | string | null, lang: string): string {
  if (value === null || value === '') return ''
  const date = typeof value === 'number'
    ? new Date(value * 1000)
    : new Date(/^\d+$/.test(value) ? Number(value) * 1000 : value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

function Card({ title, icon, children }: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-md border border-white/10 bg-card/80 p-6">
      <h2 className="flex items-center gap-2 font-display text-[13px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        {icon}
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  )
}

export default function HomeProfile() {
  const { T, lang } = useLanguage()
  const { user, loading, refresh, logout } = useHomeAuth()
  const [params, setParams] = useSearchParams()
  // One shared boolean latched the whole card: after "become a supporter" sends
  // the browser to Whop, `busy` stays true, and coming *back* restores the page
  // from bfcache still disabled — which is how the unlink button became dead.
  // Naming the in-flight action instead keeps each button independent, and the
  // pageshow reset below clears it on a restored page.
  const [busy, setBusy] = useState<'' | 'checkout' | 'unlink' | 'cancel' | 'resume'>('')
  const [error, setError] = useState('')
  // The cancel confirmation is inline rather than window.confirm: a browser
  // dialog cannot say which date the perks run to, and that date is the whole
  // reason somebody hesitates over the button.
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelError, setCancelError] = useState('')

  // Steam bounces back with ?steam=linked|failed|taken. Read it once, then drop
  // it from the URL so a refresh does not replay a stale banner.
  const steamResult = params.get('steam')
  useEffect(() => {
    if (!steamResult) return
    if (steamResult === 'linked') void refresh()
    const next = new URLSearchParams(params)
    next.delete('steam')
    setParams(next, { replace: true })
  }, [steamResult, params, setParams, refresh])

  const [banner, setBanner] = useState('')
  useEffect(() => {
    if (steamResult === 'failed') setBanner(T.profile.steamFailed)
    else if (steamResult === 'taken') setBanner(T.profile.steamTaken)
  }, [steamResult, T])

  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) setBusy('') }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  async function startCheckout() {
    setBusy('checkout'); setError('')
    try {
      const res = await fetch('/api/home/supporter/checkout', {
        method: 'POST', credentials: 'include',
      })
      if (!res.ok) throw new Error(String(res.status))
      const { purchase_url } = await res.json()
      window.location.href = purchase_url
    } catch {
      setError(T.supporter.ctaError)
      setBusy('')
    }
  }

  /** Cancel and resume are the same call with different words attached, so the
   *  in-flight state, the error line and the re-read stay in one place. */
  async function writeSupporter(action: 'cancel' | 'resume') {
    const blocked = action === 'cancel'
      ? T.profile.supporterCancelBlocked
      : T.profile.supporterResumeBlocked
    const failed = action === 'cancel'
      ? T.profile.supporterCancelError
      : T.profile.supporterResumeError

    setBusy(action); setCancelError('')
    try {
      const res = await fetch(`/api/home/supporter/${action}`, {
        method: 'POST', credentials: 'include',
      })
      // 409 is a key that cannot write — a fault on our side that retrying will
      // not fix, so it gets its own line instead of "try again".
      if (!res.ok) { setCancelError(res.status === 409 ? blocked : failed); return }
      setConfirmCancel(false)
      // Read the session back rather than patching the card from the response:
      // the state the profile shows always comes from /api/home/me.
      await refresh()
    } catch {
      setCancelError(failed)
    } finally {
      setBusy('')
    }
  }

  async function unlinkSteam() {
    setBusy('unlink')
    try {
      await fetch('/api/home/steam', { method: 'DELETE', credentials: 'include' })
      await refresh()
    } finally {
      setBusy('')
    }
  }

  const body = () => {
    if (loading) {
      return <p className="text-sm text-muted-foreground">{T.profile.loading}</p>
    }

    if (!user) {
      return (
        <Card title={T.profile.signedOutTitle}>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {T.profile.signedOutBody}
          </p>
          <a href={HOME_LOGIN_URL} className="mt-5 inline-block">
            <Button size="lg" className="gap-2 font-semibold">
              <DiscordIcon size={16} />
              {T.profile.signIn}
            </Button>
          </a>
        </Card>
      )
    }

    const steamLinked = user.steam.linked
    const s = user.supporter

    return (
      <div className="grid gap-5">
        {/* ── Discord ─────────────────────────────────────────────────────── */}
        <Card title={T.profile.discordTitle} icon={<DiscordIcon size={13} />}>
          <div className="flex items-center gap-4">
            <img
              src={user.discord.avatar_url}
              alt=""
              className="h-12 w-12 shrink-0 rounded-full border border-white/10"
            />
            <div className="min-w-0">
              <p className="truncate font-display text-[15px] font-bold tracking-tight">
                {user.discord.name}
              </p>
              <p className="truncate text-[12px] text-muted-foreground">
                @{user.discord.username}
              </p>
            </div>
            <button
              onClick={() => void logout()}
              className="ml-auto flex shrink-0 items-center gap-1.5 rounded-sm border border-white/12 bg-white/5 px-3 py-1.5 text-[11px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" />
              {T.profile.signOut}
            </button>
          </div>
          <p className="mt-4 text-[12px] leading-relaxed text-muted-foreground/80">
            {T.profile.discordNote}
          </p>
        </Card>

        {/* ── Steam ───────────────────────────────────────────────────────── */}
        <Card title={T.profile.steamTitle} icon={<SteamIcon size={13} />}>
          {steamLinked ? (
            <>
              <p className="text-[15px] font-semibold tracking-tight">
                {user.steam.name || T.profile.steamNoName}
              </p>
              <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                {user.steam.verified
                  ? <><ShieldCheck className="h-3.5 w-3.5 text-primary" />{T.profile.steamVerified}</>
                  : <><TriangleAlert className="h-3.5 w-3.5 text-amber-400" />{T.profile.steamUnverified}</>}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <a href="/api/home/steam/link">
                  <Button variant="outline" size="sm" className="gap-2">
                    <SteamIcon size={14} />
                    {user.steam.verified ? T.profile.steamRelink : T.profile.steamLink}
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === 'unlink'}
                  onClick={() => void unlinkSteam()}
                >
                  {T.profile.steamUnlink}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                {T.profile.steamNone}
              </p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground/70">
                {T.profile.steamWhy}
              </p>
              <a href="/api/home/steam/link" className="mt-5 inline-block">
                <Button size="sm" className="gap-2 font-semibold">
                  <SteamIcon size={14} />
                  {T.profile.steamLink}
                </Button>
              </a>
            </>
          )}
        </Card>

        {/* ── Supporter ───────────────────────────────────────────────────── */}
        {/* `s || SUPPORTER_ENABLED`, not one or the other: an existing
            membership is still a thing to manage while sales are off. */}
        {(s || SUPPORTER_ENABLED) && (
          <Card title={T.profile.supporterTitle}>
            {s ? (
              <>
                <p className="flex items-center gap-2 font-display text-[15px] font-bold tracking-tight">
                  <span
                    className={`inline-flex h-4 w-4 items-center justify-center rounded-sm border ${
                      s.active
                        ? 'border-primary/40 bg-primary/10'
                        : 'border-white/15 bg-white/5'
                    }`}
                  >
                    {s.active && <Check className="h-2.5 w-2.5 text-primary" />}
                  </span>
                  {s.active ? T.profile.supporterActive : T.profile.supporterInactive}
                </p>
                {formatDate(s.renews_at, lang) && (
                  <p className="mt-2 text-[13px] text-muted-foreground">
                    {(s.cancel_at_period_end ? T.profile.supporterEnds : T.profile.supporterRenews)
                      .replace('{date}', formatDate(s.renews_at, lang))}
                  </p>
              )}

              {/* Cancelling belongs here, not behind an outbound link: sending
                  somebody to a third-party dashboard to stop paying us is the
                  part of the flow they are least willing to hunt for. What stays
                  on Whop is what only Whop can do — receipts and the card. */}
              {/* Inside the paid period a cancellation is still reversible, and
                  the button that reverses it belongs next to the date it runs
                  to — that date is the whole question the supporter is asking. */}
              {s.active && s.cancel_at_period_end && (
                <div className="mt-5">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === 'resume'}
                    onClick={() => void writeSupporter('resume')}
                  >
                    {T.profile.supporterResume}
                  </Button>
                  {cancelError && (
                    <p className="mt-3 text-[12px] leading-relaxed text-destructive">
                      {cancelError}
                    </p>
                  )}
                </div>
              )}

              {s.active && !s.cancel_at_period_end && (
                confirmCancel ? (
                  <div className="mt-5 rounded-sm border border-white/12 bg-white/5 p-4">
                    <p className="font-display text-[13px] font-bold tracking-tight">
                      {T.profile.supporterCancelTitle}
                    </p>
                    <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                      {formatDate(s.renews_at, lang)
                        ? T.profile.supporterCancelBody
                            .replace('{date}', formatDate(s.renews_at, lang))
                        : T.profile.supporterCancelBodyNoDate}
                    </p>
                    {/* Said before the click, not after: knowing the decision is
                        reversible is what makes it safe to make. */}
                    {formatDate(s.renews_at, lang) && (
                      <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground/70">
                        {T.profile.supporterResumeNote
                          .replace('{date}', formatDate(s.renews_at, lang))}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={busy === 'cancel'}
                        onClick={() => void writeSupporter('cancel')}
                      >
                        {T.profile.supporterCancelConfirm}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy === 'cancel'}
                        onClick={() => { setConfirmCancel(false); setCancelError('') }}
                      >
                        {T.profile.supporterCancelKeep}
                      </Button>
                    </div>
                    {cancelError && (
                      <p className="mt-3 text-[12px] leading-relaxed text-destructive">
                        {cancelError}
                      </p>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-5"
                    onClick={() => setConfirmCancel(true)}
                  >
                    {T.profile.supporterCancel}
                  </Button>
                )
              )}

              <a
                href="https://whop.com/orders"
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex w-fit items-center gap-1.5 text-[12px] font-semibold text-foreground/70 transition-colors hover:text-foreground"
              >
                {T.profile.supporterBilling}
                <ExternalLink className="h-3 w-3" />
              </a>
            </>
          ) : (
            <>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                {T.profile.supporterNone}
              </p>
              {/* Steam first: the backend refuses checkout without a verified
                  link, so the button would only produce an error here. */}
              {steamLinked && user.steam.verified ? (
                <Button
                  size="lg"
                  disabled={busy === 'checkout'}
                  onClick={() => void startCheckout()}
                  className="mt-5 font-semibold"
                >
                  {T.profile.supporterCta}
                </Button>
              ) : (
                <p className="mt-4 text-[12px] leading-relaxed text-muted-foreground/70">
                  {T.supporter.ctaLinkSteamNote}
                </p>
              )}
              {error && <p className="mt-3 text-[12px] text-destructive">{error}</p>}
            </>
          )}
        </Card>
        )}
      </div>
    )
  }

  return (
    <HomeShell pageTitle={T.profile.pageTitle}>
      <section className="mx-auto w-full max-w-3xl px-5 pb-20 pt-16 md:pt-20">
        <h1 className="font-display text-3xl font-extrabold uppercase leading-[0.98] tracking-tight md:text-5xl">
          {T.profile.title}
        </h1>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground/80">
          {T.profile.lead}
        </p>

        {banner && (
          <p className="mt-6 rounded-sm border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[13px] text-amber-200">
            {banner}
          </p>
        )}

        <div className="mt-10">{body()}</div>
      </section>
    </HomeShell>
  )
}
